/**
 * The booth station, opening and closing the booth (split out of BoothStation.tsx
 * by interaction — James, Oct 21, item 5; no behavior change).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { formatTime } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';

export interface Opening {
  expected: number;
  shifts: number;
  stations: { label: string; me: boolean; lastSeenAt: string | null; says: string }[];
  says: string;
}

/**
 * **Open the booth** (`Booth.dc.html` openday, the ninth overnight 2d): before the doors — the
 * network (the last sync), the printer (a test tag through the browser's print), the scanner (a
 * scan lands in the search box), the day's roster, and every station with when it was last
 * seen. A station that is not answering is a sentence.
 */
export function OpenTheBooth({
  synced,
  day,
  onTestTag,
  onPreview,
  printer,
}: {
  synced: Date | null;
  day: string;
  onTestTag: () => void;
  onPreview: () => void;
  /** False on the walk-around unit (state 11): no printer here, so no test tag. */
  printer: boolean;
}): ReactNode {
  const [o, setO] = useState<Opening | null>(null);
  const [desks, setDesks] = useState<{
    shift: string | null;
    rows: { personId: string; name: string; says: string }[];
  } | null>(null);
  const [trouble, setTrouble] = useState<string | null>(null);
  useEffect(() => {
    api<Opening>('GET', '/volunteers/booth/opening')
      .then(setO)
      .catch((e: unknown) => {
        setTrouble(sentence(e));
      });
    api<{ shift: string | null; rows: { personId: string; name: string; says: string }[] }>(
      'GET',
      '/volunteers/booth/desk/roster',
    )
      .then(setDesks)
      .catch(() => undefined);
  }, []);
  return (
    <div className="booth__found" data-testid="booth-open">
      {trouble ? <p className="booth__refusal">{trouble}</p> : null}
      <section className="booth__card">
        <div className="booth__cardhead">
          BEFORE THE DOORS
          <span className="booth__cardsub">each station · {day}</span>
        </div>
        {o?.stations.map((x) => {
          const fresh =
            x.me || (x.lastSeenAt && Date.now() - new Date(x.lastSeenAt).getTime() < 5 * 60_000);
          return (
            <div className="booth__row" key={x.label}>
              <span className="booth__rowa">
                <b>{x.label}</b>
                <span className="booth__rowb">
                  {x.me
                    ? synced
                      ? `this station · synced ${formatTime(synced)}`
                      : 'this station · not answered yet'
                    : x.says}
                  {x.lastSeenAt && !x.me ? ` · last seen ${formatTime(x.lastSeenAt)}` : ''}
                </span>
              </span>
              <span className="booth__rowc">
                {x.me && printer
                  ? 'Print a test tag — the printer is ready when it comes out.'
                  : ''}
              </span>
              <span className={`booth__chip booth__chip--${fresh ? 'ok' : 'warn'}`}>
                {fresh ? 'GREEN' : 'CHECK'}
              </span>
              {x.me ? (
                <>
                  {printer ? (
                    <button type="button" className="booth__act" onClick={onTestTag}>
                      Print a test tag
                    </button>
                  ) : null}
                  <button type="button" className="booth__act" onClick={onPreview}>
                    Preview the tag
                  </button>
                </>
              ) : null}
            </div>
          );
        })}
        <div className="booth__cardfoot">
          A scanner types into the search box: scan any tag and it lands there. Nothing to press.
        </div>
      </section>
      <section className="booth__card booth__card--red">
        <div className="booth__cardhead">
          TODAY AT THE DESKS
          <span className="booth__cardsub">the booth shift · they find their own name</span>
        </div>
        {desks?.rows.length ? (
          desks.rows.map((r) => (
            <div className="booth__row" key={r.personId}>
              <span className="booth__rowa">
                <b>{r.name}</b>
                <span className="booth__rowb">{desks.shift ?? 'the booth shift'}</span>
              </span>
              <span className="booth__rowc">{r.says}</span>
              <span className="booth__chip booth__chip--info">TODAY</span>
            </div>
          ))
        ) : (
          <div className="booth__row">
            <span className="booth__rowc">
              Nobody is on a booth shift today — anyone can find their name at a station.
            </span>
          </div>
        )}
        <div className="booth__cardfoot">{o?.says ?? 'Reading the roster…'}</div>
      </section>
      <section className="booth__card booth__card--gray">
        <div className="booth__cardhead">
          PRINTED AT OPEN
          <span className="booth__cardsub">the paper fallback</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>The booth sheet</b>
            <span className="booth__rowb">names by hour · a signature column</span>
          </span>
          <button
            type="button"
            className="booth__act"
            onClick={() => {
              window.open('/print/booth-sheet/today', 'cider-print');
            }}
          >
            Print the booth sheet
          </button>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>The shelter roll</b>
            <span className="booth__rowb">on site and on shift, a tick each · for a hold</span>
          </span>
          <button
            type="button"
            className="booth__act"
            onClick={() => {
              window.open('/print/shelter-roll/today', 'cider-print');
            }}
          >
            Print the shelter roll
          </button>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>The table card</b>
            <span className="booth__rowb">I’m here · what to have ready · where to go after</span>
          </span>
          <button
            type="button"
            className="booth__act"
            onClick={() => {
              window.open('/print/booth-card/today', 'cider-print');
            }}
          >
            Print the table card
          </button>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>The paper waiver pad</b>
            <span className="booth__rowb">the same text as the guest screen</span>
          </span>
          <button
            type="button"
            className="booth__act"
            onClick={() => {
              window.open('/print/waiver-pad/20', 'cider-print');
            }}
          >
            Print the waiver pad
          </button>
        </div>
      </section>
    </div>
  );
}
export interface Closing {
  stillIn: { name: string; area: string; startsAt: string; endsAt: string }[];
  checkedIn: number;
  checkedOut: number;
  /** "17 open — Serving 6, Pub 4, Kids Tent 7": the year's rows still open, never a block. */
  openRows: string;
  says: string;
}

/**
 * **Close the booth** (`Booth.dc.html` closeday, 2d): who is still checked in, the day's count,
 * and one line in the record. Closing the booth closes nobody's check-in — nothing closes on its
 * own (James, Oct 6: hours are a human's call). The rows still open after their shifts are
 * counted by area, as information — they never stop the booth closing.
 */
export function CloseTheBooth({
  onClosed,
  queued,
}: {
  onClosed: () => void;
  queued: number;
}): ReactNode {
  const [c, setC] = useState<Closing | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [trouble, setTrouble] = useState<string | null>(null);
  useEffect(() => {
    api<Closing>('GET', '/volunteers/booth/closing')
      .then(setC)
      .catch((e: unknown) => {
        setTrouble(sentence(e));
      });
  }, []);
  const byArea = new Map<string, string[]>();
  for (const p of c?.stillIn ?? []) byArea.set(p.area, [...(byArea.get(p.area) ?? []), p.name]);
  return (
    <div className="booth__found" data-testid="booth-close">
      <div className="booth__strip">
        <div className="booth__stat">
          <b>{c ? String(c.checkedIn) : '—'}</b>
          <span>CHECKED IN</span>
        </div>
        <div className="booth__stat booth__stat--ok">
          <b>{c ? String(c.checkedOut) : '—'}</b>
          <span>CHECKED OUT</span>
        </div>
        <div className="booth__stat booth__stat--warn">
          <b>{c ? String(c.stillIn.length) : '—'}</b>
          <span>NEVER CHECKED OUT</span>
        </div>
      </div>
      {trouble ? <p className="booth__refusal">{trouble}</p> : null}
      <section className="booth__card booth__card--gold">
        <div className="booth__cardhead">
          NEVER CHECKED OUT
          <span className="booth__cardsub">
            {c ? `${String(c.stillIn.length)} · ` : ''}each goes to its area chair · the answer is a
            state on the person
          </span>
        </div>
        {c ? (
          byArea.size ? (
            [...byArea].map(([area, names]) => (
              <div className="booth__row" key={area}>
                <span className="booth__rowa">
                  <b>
                    {area} · {names.length}
                  </b>
                  <span className="booth__rowb">{names.join(' · ')}</span>
                </span>
                <span className="booth__rowc">
                  The area chair answers: worked to the end, left at a time, or walked off.
                </span>
                <span className="booth__chip booth__chip--warn">OPEN</span>
              </div>
            ))
          ) : (
            <div className="booth__row">
              <span className="booth__rowc">Nobody is still checked in.</span>
            </div>
          )
        ) : (
          <div className="booth__row">
            <span className="booth__rowc">Reading…</span>
          </div>
        )}
        {c && c.openRows !== 'Nothing open.' ? (
          <div className="booth__row" data-testid="booth-open-rows">
            <span className="booth__rowa">
              <b>Needs work</b>
            </span>
            <span className="booth__rowc">
              {c.openRows} · the chairs and the volunteer team settle each one
            </span>
          </div>
        ) : null}
        <div className="booth__cardfoot">
          Nothing closes on its own. The chair’s answer is the credit. Closing the booth closes
          nobody.
        </div>
      </section>
      <section className="booth__card booth__card--gray">
        <div className="booth__cardhead">
          THE RECORD
          <span className="booth__cardsub">one line at close</span>
        </div>
        <div className="booth__row">
          <span className="booth__rowa">
            <b>The day’s counts, and who never checked out</b>
          </span>
        </div>
      </section>
      {done ? (
        <p className="booth__done" role="status">
          {done}
        </p>
      ) : (
        <button
          type="button"
          className="booth__big booth__big--navy"
          onClick={() => {
            if (queued > 0) {
              setTrouble(
                `${String(queued)} ${queued === 1 ? 'action is' : 'actions are'} still waiting to sync. The booth closes when the network is back and the queue is empty.`,
              );
              return;
            }
            api<Closing>('POST', '/volunteers/booth/close', { queued })
              .then((r) => {
                setDone(`${r.says}. The line is in the record.`);
                onClosed();
              })
              .catch((e: unknown) => {
                setTrouble(sentence(e));
              });
          }}
        >
          Close the booth
        </button>
      )}
    </div>
  );
}
